import { WorldMaker } from '../WorldMaker';

// Interface for behavior attachment rules
export interface AttachmentRule {
  type: 'element' | 'class' | 'root';
  selector: string;
}

// Base Behavior class that all behaviors extend
export abstract class Behavior {
  protected element: Element;
  protected world: WorldMaker;
  
  constructor(element: Element, world: WorldMaker) {
    this.element = element;
    this.world = world;
  }

  // Helper method to get the root element from the World
  protected getRootElement(): Element | null {
    return this.world.getRootElement();
  }

  // Called when the behavior is attached to an element
  onAttach(): void {
    // Override in subclasses if needed
  }

  // Called when the behavior is detached from an element
  onDetach(): void {
    // Override in subclasses if needed
  }

  // Called when an attribute changes on the element
  onAttributeChange(attributeName: string, oldValue: string | null, newValue: string | null): void {
    // Override in subclasses if needed
  }

  // Called every frame while the behavior is active
  tick(deltaTime: number): void {
    // Override in subclasses
  }

  // Static method to define attachment rules for this behavior
  static getAttachmentRules(): AttachmentRule[] {
    // Override in subclasses to define when this behavior should attach
    return [];
  }
}

// Interface for behavior constructor
export interface BehaviorConstructor {
  new (element: Element, world: WorldMaker): Behavior;
  getAttachmentRules(): AttachmentRule[];
}

// Extend Element prototype with behavior helper methods
declare global {
  interface Element {
    getBehavior<T extends Behavior>(behaviorClass: new (element: Element, world: WorldMaker) => T): T | null;
    hasBehavior(behaviorClass: BehaviorConstructor): boolean;
    getAllBehaviors(): Behavior[];
  }
} 